import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Card, Combobox, ComboboxOption } from '@forge/common';
import { ActivityAppDispatch, useActivitySelector } from '../Redux/activityStore';
import {
  selectAccessibleCustomers,
  selectAccessibleCustomersError,
  selectAccessibleCustomersLoading,
} from '../Redux/activitySelectors';
import { fetchAccessibleCustomers } from '../Redux/activitySlice';
import {
  ACTIVITY_SEARCH_DEBOUNCE_MS,
  buildVisibleCustomerOptions,
  filterCustomerOptions,
  getRemainingCustomerOptionsCount,
  mapAccessibleCustomerOptions,
} from '../utils';
import { useDebouncedValue } from './shared/useDebouncedValue';
import ActivityTabContainerWithToast from './ActivityTabContainer';
import ActivityErrorState from './shared/ActivityErrorState';
import './ActivityLandingPage.scss';

const SELECTED_ORGANIZATION_KEY = 'selected-organization-id' as const;

interface ActivityLandingPageProps {
}

interface ICustomerOption {
  id: string;
  name: string;
}

const MIN_CUSTOMER_SEARCH_CHARACTERS = 3;
const CUSTOMER_OPTIONS_PAGE_SIZE = 15;
const LOAD_MORE_DEBOUNCE_MS = 250;

const ActivityLandingPage: React.FC<ActivityLandingPageProps> = () => {
  const dispatch = useDispatch<ActivityAppDispatch>();
  const { t } = useTranslation();

  const [customerOrganization, setCustomerOrganization] = useState<ICustomerOption>({} as ICustomerOption);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleOptionsCount, setVisibleOptionsCount] = useState(CUSTOMER_OPTIONS_PAGE_SIZE);
  const [isLoadMoreCoolingDown, setIsLoadMoreCoolingDown] = useState(false);
  const startedAccessibleLoadOrgIdRef = useRef<string | null>(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, ACTIVITY_SEARCH_DEBOUNCE_MS);
  const effectiveSearchTerm = searchTerm === '' ? '' : debouncedSearchTerm;

  const accessibleCustomers = useActivitySelector(selectAccessibleCustomers);
  const accessibleCustomersError = useActivitySelector(selectAccessibleCustomersError);
  const isLoadingCustomers = useActivitySelector(selectAccessibleCustomersLoading);

  const parentOrgId = sessionStorage.getItem(SELECTED_ORGANIZATION_KEY) || '';
  const canFetchAccessibleCustomers = Boolean(parentOrgId);

  const currentOrgCustomerOptions = accessibleCustomers ?? [];

  const currentSiCustomerOptions = useMemo(
    () => mapAccessibleCustomerOptions(currentOrgCustomerOptions),
    [currentOrgCustomerOptions]
  );

  const resetCustomerSelection = useCallback(() => {
    setCustomerOrganization({} as ICustomerOption);
  }, []);

  const resetComboboxView = useCallback(() => {
    setVisibleOptionsCount(CUSTOMER_OPTIONS_PAGE_SIZE);
    setSearchTerm('');
  }, []);

  useEffect(() => {
    resetCustomerSelection();
  }, [parentOrgId, resetCustomerSelection]);

  useEffect(() => {
    if (!canFetchAccessibleCustomers) {
      return;
    }

    startedAccessibleLoadOrgIdRef.current = parentOrgId;
    dispatch(fetchAccessibleCustomers({ parentOrgId }));
  }, [
    canFetchAccessibleCustomers,
    dispatch,
    parentOrgId,
  ]);

  const filteredCustomerOrgOptions = useMemo(() => {
    return filterCustomerOptions(currentSiCustomerOptions, effectiveSearchTerm);
  }, [currentSiCustomerOptions, effectiveSearchTerm]);

  const hasSearchQuery = effectiveSearchTerm.trim().length > 0;

  useEffect(() => {
    setVisibleOptionsCount(CUSTOMER_OPTIONS_PAGE_SIZE);
  }, [effectiveSearchTerm, currentSiCustomerOptions]);

  const visibleCustomerOrgOptions = useMemo(() => {
    return buildVisibleCustomerOptions(
      filteredCustomerOrgOptions,
      visibleOptionsCount,
      customerOrganization?.id,
      hasSearchQuery
    );
  }, [filteredCustomerOrgOptions, visibleOptionsCount, hasSearchQuery, customerOrganization?.id, currentSiCustomerOptions]);

  const hasMoreOptions = visibleOptionsCount < filteredCustomerOrgOptions.length;
  const hasUsableCustomerOptions = currentSiCustomerOptions.length > 0;
  const isComboboxDisabled = !hasUsableCustomerOptions;
  const remainingCustomerOptionsCount = getRemainingCustomerOptionsCount(
    filteredCustomerOrgOptions.length,
    visibleOptionsCount
  );

  const handleLoadMore = useCallback(() => {
    if (isLoadMoreCoolingDown || !hasMoreOptions) {
      return;
    }

    setIsLoadMoreCoolingDown(true);
    setVisibleOptionsCount(prev => prev + CUSTOMER_OPTIONS_PAGE_SIZE);

    window.setTimeout(() => {
      setIsLoadMoreCoolingDown(false);
    }, LOAD_MORE_DEBOUNCE_MS);
  }, [isLoadMoreCoolingDown, hasMoreOptions]);

  const handleRefreshAccessibleCustomers = useCallback(() => {
    if (!canFetchAccessibleCustomers) {
      return;
    }
    dispatch(fetchAccessibleCustomers({ parentOrgId }));
  }, [canFetchAccessibleCustomers, dispatch, parentOrgId]);

  const getSelectedCustomerOption = (customerId: string) =>
    currentSiCustomerOptions.find(customer => customer.id === customerId);

  const allCustomerIds = useMemo(
    () => (currentOrgCustomerOptions || []).map((customer: ICustomerOption) => customer.id),
    [currentOrgCustomerOptions]
  );
  // Block query until accessible-customer fetch has started for current org
  // and while accessible-customer request is still loading.
  const shouldBlockQueryIds = canFetchAccessibleCustomers
    && (startedAccessibleLoadOrgIdRef.current !== parentOrgId || isLoadingCustomers);

  const effectiveCustomerIds = useMemo(() => {
    if (!parentOrgId || shouldBlockQueryIds) {
      return [];
    }
    if (customerOrganization?.id) {
      return [customerOrganization.id];
    }
    if (allCustomerIds.length === 0) {
      return [parentOrgId];
    }
    return [parentOrgId, ...allCustomerIds];
  }, [
    parentOrgId,
    shouldBlockQueryIds,
    customerOrganization?.id,
    allCustomerIds,
  ]);

  const effectiveCustomerId = customerOrganization?.id || '';
  // When a specific customer is selected, SI org id must not be sent to values API.
  const effectiveSiOrgId = customerOrganization?.id ? undefined : (parentOrgId || undefined);
  const selectedCustomerOption = customerOrganization?.name ? customerOrganization : undefined;

  return (
    <div className="activity-landing-page">
      <div className="activity-landing-header-card">
        <div className="activity-landing-header-row">
          <div className="activity-landing-header-left">
            <h2 className="activity-landing-title">{t('activityLogSidebarTitle')}</h2>
            <h4 className="activity-landing-subtitle">{t('activityLogSidebarSubtitle')}</h4>
          </div>

          <div className="activity-landing-header-right">
            <>
            <div className="activity-landing-dropdown-label">{t('customer')}</div>
            <Combobox
              height={5}
              id="RootOrganization"
              minCharacters={MIN_CUSTOMER_SEARCH_CHARACTERS}
              multiSum
              autoComplete="off"
              placeholder={`${t('selectCustomer')}${currentSiCustomerOptions.length > 0 ? ` (${currentSiCustomerOptions.length})` : ''}`}
              action={hasMoreOptions ? (
                <Button
                  variant="secondary"
                  size="regular"
                  disabled={isLoadMoreCoolingDown}
                  onClick={handleLoadMore}
                >
                  {`${t('loadMore')} (${Math.min(CUSTOMER_OPTIONS_PAGE_SIZE, remainingCustomerOptionsCount)})`}
                </Button>
              ) : undefined}
              value={
                selectedCustomerOption
                  ? [{ label: selectedCustomerOption.name, value: selectedCustomerOption.id }]
                  : undefined
              }
              onBlur={() => {
                resetComboboxView();
              }}
              onSearch={(value) => {
                setSearchTerm(value || '');
              }}
              onChange={(selectedValues) => {
                const selectedValue = selectedValues?.length
                  ? selectedValues[0]?.value?.toString()
                  : '';

                const customer = getSelectedCustomerOption(selectedValue || '');
                setCustomerOrganization(customer || ({} as ICustomerOption));
                resetComboboxView();
              }}
              className="activity-landing-dropdown-control"
              isLoading={isLoadingCustomers}
              disabled={isComboboxDisabled}
            >
              {visibleCustomerOrgOptions.map(item => (
                <ComboboxOption
                  key={item.id}
                  value={item.id}
                  label={item.name}
                  className="activity-landing-dropdown-option"
                />
              ))}
            </Combobox>
           </>
          </div>
        </div>
      </div>

      <div className="activity-landing-table-wrap">
        <Card className="activity-landing-table-card">
          {accessibleCustomersError ? (
            <ActivityErrorState
              onRefresh={handleRefreshAccessibleCustomers}
              message={t('activityLogFetchError')}
              className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-4"
            />
          ) : (
            <ActivityTabContainerWithToast
              selectedOrganizationId={effectiveCustomerId}
              selectedOrganizationIds={effectiveCustomerIds}
              siOrgId={effectiveSiOrgId}
              isLoadingAccessibleCustomers={isLoadingCustomers}
              activityTableTitle={t('activityLogListTitle')}
              isSIFlow
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default ActivityLandingPage;
